import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { AiPersistedAction } from './AiChatPanel';

// The contexts the assistant serves. Which one is active is decided by whichever
// trip tab is currently mounted (see `useAiPanel`), and is sent to the backend
// only as a routing *hint* — the orchestrator picks the actual specialist.
// `general` is a tab with no specialist behind it (meals, notes, gallery): the
// assistant is still there, and sends no hint at all.
export type AiPanelContextKind = 'places' | 'itinerary' | 'allroutes' | 'packing' | 'tips' | 'bookings' | 'expenses' | 'general';

export type AiActionHandler = (actions: AiPersistedAction[]) => void;

interface AiPanelValue {
  // Active context, or null when the current tab has no assistant.
  context: AiPanelContextKind | null;
  // Most recent non-null context. Lets the dock keep the panel (and its
  // conversation) mounted while the user is on a tab without an assistant.
  lastContext: AiPanelContextKind | null;
  setContext: (c: AiPanelContextKind | null) => void;
  // Registry of the currently-mounted tabs' action handlers, keyed by context.
  // The central dispatcher (useAiActionDispatch) routes each persisted proposal
  // to the handler of the domain it belongs to — regardless of which tab is
  // active — so a proposal accepted from any tab lands in the right place.
  // Domains whose tab is unmounted have no entry: the entity is still persisted
  // server-side and shows up when that tab is next visited (it refetches).
  handlersRef: React.MutableRefObject<Map<AiPanelContextKind, AiActionHandler>>;
  collapsed: boolean;
  setCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
  /**
   * A prompt handed to the panel from somewhere else in the trip — an empty
   * day asking to be planned, a suggestion chip. `nonce` is what makes asking
   * the same thing twice actually ask twice.
   */
  request: AiRequest | null;
  /**
   * Open the panel with this prompt. `send: true` submits it straight away —
   * for a button whose whole meaning is the question, where making the user
   * press Enter on text they did not type is a step for nothing.
   */
  askAi: (text: string, opts?: { send?: boolean }) => void;
  clearRequest: () => void;
  /**
   * ADR-0028: bumped when an accepted card changed an area's data on the
   * server. A tab adds its area's number to the effect that loads it
   * (useAiDataVersion), so it re-reads exactly what the assistant changed.
   */
  dataVersions: Record<string, number>;
  bumpData: (area: string) => void;
}

export interface AiRequest {
  text: string;
  send: boolean;
  nonce: number;
}

const Ctx = createContext<AiPanelValue | null>(null);

export function AiPanelProvider({ children }: { children: ReactNode }) {
  const [context, setContextState] = useState<AiPanelContextKind | null>(null);
  const [lastContext, setLastContext] = useState<AiPanelContextKind | null>(null);
  const [collapsed, setCollapsed] = useState(true); // starts as the slim rail
  const handlersRef = useRef<Map<AiPanelContextKind, AiActionHandler>>(new Map());
  const [request, setRequest] = useState<AiRequest | null>(null);
  const nonce = useRef(0);

  const askAi = useCallback((text: string, opts?: { send?: boolean }) => {
    nonce.current += 1;
    setRequest({ text, send: opts?.send ?? false, nonce: nonce.current });
    // A prompt nobody can see is not a prompt.
    setCollapsed(false);
  }, []);

  const clearRequest = useCallback(() => setRequest(null), []);

  const [dataVersions, setDataVersions] = useState<Record<string, number>>({});
  const bumpData = useCallback((area: string) => {
    setDataVersions(v => ({ ...v, [area]: (v[area] ?? 0) + 1 }));
  }, []);

  const setContext = useCallback((c: AiPanelContextKind | null) => {
    setContextState(c);
    if (c) setLastContext(c);
  }, []);

  const value = useMemo<AiPanelValue>(
    () => ({ context, lastContext, setContext, handlersRef, collapsed, setCollapsed, request, askAi, clearRequest, dataVersions, bumpData }),
    [context, lastContext, setContext, collapsed, request, askAi, clearRequest, dataVersions, bumpData],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAiPanelState() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAiPanelState outside AiPanelProvider');
  return v;
}

/**
 * Called by a trip tab to make the assistant available in its context and,
 * optionally, to apply persisted proposals to its own local state. Passing
 * `null` (e.g. a map in a non-route mode) hides the assistant.
 *
 * `onAction` is only needed for tabs that hold their data in local state
 * (venues, packing, tips). Itinerary data lives in TripContext and is applied
 * centrally by the dispatcher, so ItineraryTab omits `onAction`. The handler is
 * read through the registry ref, so it may change identity every render without
 * re-registering.
 *
 * `handles` exists because a tab's context and the domains it owns are not
 * always the same thing: the map's routes view talks to the `allroutes` agent
 * but is also what's on screen when a venue is created, so it registers for
 * `places` too — otherwise the new place lands in a tab nobody is looking at.
 */
export function useAiPanel(
  config: {
    context: AiPanelContextKind;
    onAction?: AiActionHandler;
    handles?: AiPanelContextKind[];
  } | null,
) {
  const { setContext, handlersRef } = useAiPanelState();
  const onAction = config?.onAction ?? null;
  const context = config?.context ?? null;
  const handles = config?.handles;
  // The registry is keyed by domain, so the join is what identity depends on —
  // not the array, which is a new reference every render.
  const domainKey = (handles ?? (context ? [context] : [])).join(',');

  // Keep the latest handler in the registry under every domain this tab owns,
  // without re-registering: runs after every render, cleared when it unmounts.
  useEffect(() => {
    const domains = domainKey ? (domainKey.split(',') as AiPanelContextKind[]) : [];
    if (domains.length === 0) return;
    for (const domain of domains) {
      if (onAction) handlersRef.current.set(domain, onAction);
      else handlersRef.current.delete(domain);
    }
    return () => { for (const domain of domains) handlersRef.current.delete(domain); };
  });

  useEffect(() => {
    setContext(context);
    return () => setContext(null);
  }, [context, setContext]);
}

/** How many times the assistant changed this area's data (0 = never): an effect dependency. */
export function useAiDataVersion(area: string): number {
  // Outside the trip shell (a component rendered on its own) nothing can change it.
  return useContext(Ctx)?.dataVersions[area] ?? 0;
}
